import { supabase } from './supabaseClient';
import { requestNotificationPermission } from './notificationService';

export const urlBase64ToUint8Array = (base64String) => {
  const raw = String(base64String || '').trim();
  if (!raw) return new Uint8Array();

  const padding = '='.repeat((4 - (raw.length % 4)) % 4);
  const base64 = `${raw}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const output = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    output[index] = binary.charCodeAt(index);
  }
  return output;
};

export const isStandaloneDisplayMode = (win = window) => {
  const mediaStandalone = typeof win.matchMedia === 'function'
    && Boolean(win.matchMedia('(display-mode: standalone)')?.matches);
  return Boolean(mediaStandalone || win.navigator?.standalone);
};

export const isIosDevice = (win = window) => (
  /iphone|ipad|ipod/i.test(String(win.navigator?.userAgent || ''))
);

export const shouldRequestPushPermission = (win = window) => {
  if (typeof win.Notification === 'undefined') return false;
  if (isIosDevice(win) && !isStandaloneDisplayMode(win)) return false;
  return true;
};

export const canSubscribeToWebPush = (win = window) => (
  shouldRequestPushPermission(win)
  && Boolean(win.navigator?.serviceWorker)
  && Boolean(win.PushManager)
);

export const serializePushSubscription = (subscription) => {
  const json = typeof subscription?.toJSON === 'function' ? subscription.toJSON() : subscription;
  return {
    endpoint: String(json?.endpoint || ''),
    p256dh: String(json?.keys?.p256dh || ''),
    auth: String(json?.keys?.auth || ''),
  };
};

export const buildPushSubscriptionRow = ({ user, subscription, userAgent = '' } = {}) => {
  const keys = serializePushSubscription(subscription);
  return {
    user_id: user?.id || null,
    email: String(user?.email || '').trim() || null,
    endpoint: keys.endpoint,
    p256dh: keys.p256dh,
    auth: keys.auth,
    user_agent: String(userAgent || '').trim() || null,
    updated_at: new Date().toISOString(),
  };
};

export const savePushSubscription = async ({
  client = supabase,
  user,
  subscription,
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent,
} = {}) => {
  const row = buildPushSubscriptionRow({ user, subscription, userAgent });
  if (!row.user_id || !row.endpoint || !row.p256dh || !row.auth) {
    return { ok: false, reason: 'invalid_subscription' };
  }

  const { error } = await client
    .from('push_subscriptions')
    .upsert(row, { onConflict: 'endpoint' });

  if (error) {
    console.warn('Failed to save push subscription:', error.message || error);
    return { ok: false, reason: 'save_failed' };
  }

  return { ok: true };
};

const getServiceWorkerRegistration = async (win = window) => {
  if (!win.navigator?.serviceWorker) return null;
  if (typeof win.navigator.serviceWorker.getRegistration === 'function') {
    const existing = await win.navigator.serviceWorker.getRegistration();
    if (existing) return existing;
  }

  const readyPromise = win.navigator.serviceWorker.ready;
  const timeoutPromise = new Promise((resolve) => {
    setTimeout(() => resolve(null), 4000);
  });
  return Promise.race([readyPromise, timeoutPromise]);
};

export const subscribeAndSavePushSubscription = async ({
  client = supabase,
  win = window,
  vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY,
} = {}) => {
  if (!canSubscribeToWebPush(win)) {
    return { ok: false, reason: 'unsupported' };
  }

  const granted = await requestNotificationPermission();
  if (!granted) {
    return { ok: false, reason: 'denied' };
  }

  const publicKey = String(vapidPublicKey || '').trim();
  if (!publicKey) {
    return { ok: false, reason: 'missing_vapid' };
  }

  const registration = await getServiceWorkerRegistration(win);
  if (!registration?.pushManager) {
    return { ok: false, reason: 'no_service_worker' };
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const { data: { user } = {} } = await client.auth.getUser();
  if (!user) {
    return { ok: false, reason: 'unauthenticated' };
  }

  return savePushSubscription({
    client,
    user,
    subscription,
    userAgent: win.navigator?.userAgent,
  });
};
